import { useEffect, useRef, useState } from 'react'
import { X, Loader2, Save, Eye, EyeOff, Trash2, UserPlus } from 'lucide-react'
import { getPlatformSettings, updatePlatformSettings } from '../lib/api'

interface TemplatesSettingsModalProps {
  onClose: () => void
  onSaved?: (payload: { templatesVisibleToUsers: boolean; allowlistEmails: string[] }) => void
}

function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase()
}

function arraysEqualUnordered(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const bs = new Set(b)
  return a.every((x) => bs.has(x))
}

export default function TemplatesSettingsModal({ onClose, onSaved }: TemplatesSettingsModalProps) {
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [visibleToUsers, setVisibleToUsers] = useState(false)
  const [allowlist, setAllowlist] = useState<string[]>([])
  const [emailInput, setEmailInput] = useState('')

  const initialVisibleRef = useRef(false)
  const initialAllowlistRef = useRef<string[]>([])

  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const data = await getPlatformSettings('features')
        if (cancelled) return
        const settings = (data.settings ?? {}) as Record<string, unknown>
        const flag = Boolean(settings.templates_visible_to_users)
        const rawList = Array.isArray(settings.templates_allowlist_emails)
          ? (settings.templates_allowlist_emails as unknown[])
          : []
        const emails = Array.from(
          new Set(rawList.filter((e): e is string => typeof e === 'string').map(normalizeEmail).filter(Boolean)),
        )
        setVisibleToUsers(flag)
        setAllowlist(emails)
        initialVisibleRef.current = flag
        initialAllowlistRef.current = emails
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        // 404 means the features row has not been seeded yet — fine, fall through
        // to defaults; Save will create the row. Any other failure should block
        // Save so a transient read error can't silently overwrite real state.
        if (!/not found|404/i.test(msg)) {
          setError(msg || 'Failed to load settings')
          setLoadFailed(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  function handleAddEmail(e: React.FormEvent) {
    e.preventDefault()
    const normalized = normalizeEmail(emailInput)
    if (!normalized) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setError('Please enter a valid email address')
      return
    }
    if (allowlist.includes(normalized)) {
      setError(`${normalized} is already on the list`)
      return
    }
    setError(null)
    setAllowlist([...allowlist, normalized])
    setEmailInput('')
  }

  function handleRemoveEmail(email: string) {
    setAllowlist(allowlist.filter((e) => e !== email))
  }

  async function handleSave() {
    setError(null)
    setSaving(true)
    try {
      await updatePlatformSettings('features', {
        templates_visible_to_users: visibleToUsers,
        templates_allowlist_emails: allowlist,
      })
      if (!mountedRef.current) return
      onSaved?.({ templatesVisibleToUsers: visibleToUsers, allowlistEmails: allowlist })
      onClose()
    } catch (err) {
      if (!mountedRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to save')
      setSaving(false)
    }
  }

  const dirty =
    visibleToUsers !== initialVisibleRef.current
    || !arraysEqualUnordered(allowlist, initialAllowlistRef.current)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold">Templates Settings</h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading...
            </div>
          ) : (
            <>
              <label className="flex items-start gap-3 p-4 rounded-lg border border-border hover:bg-secondary/30 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleToUsers}
                  onChange={(e) => setVisibleToUsers(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {visibleToUsers ? (
                      <Eye className="w-4 h-4 text-green-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                    Show Templates to Users
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    When ON, every user sees the templates dropdown. When OFF,
                    only admins and allowlisted users below see it.
                  </p>
                </div>
              </label>

              <div className="border-t border-border mt-5 pt-5">
                <p className="text-sm font-medium mb-1">Test access</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Give specific users access even while templates are hidden for everyone else.
                </p>
                <form onSubmit={handleAddEmail} className="flex gap-2 mb-3">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="email@example.com"
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                  <button
                    type="submit"
                    disabled={!emailInput.trim()}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add
                  </button>
                </form>

                {allowlist.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No testers added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {allowlist.map((email) => (
                      <div
                        key={email}
                        className="flex items-center justify-between px-3 py-2 rounded-lg bg-secondary text-sm"
                      >
                        <span className="truncate">{email}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="p-1 rounded hover:bg-background text-muted-foreground hover:text-destructive transition-colors shrink-0"
                          aria-label={`Remove ${email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <div className="mt-4 px-3 py-2 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !dirty || loadFailed}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
